import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { criarOperacao, listarFiliais, listarAreas, obterProximoNumeroPalete, enviarDocumentoOperacao, salvarEnviosOperacao } from '../../../services/montagemPalete';
import { listarClientes } from '../../../services/clientes';
import { buscarConfiguracoesConferencia } from '../../../services/configuracoesConferencia';
import userService from '../../../services/users';
import { listarConferentes } from '../../../services/conferente';
import type { Filial, AreaEntrega, PaleteCreateData, LocalPreCarregamento, TipoPalete } from '../../../types/montagemPalete';
import type { EnvioAutomaticoConfig as EnvioConfig } from '../../../types/config';
import EnvioAutomaticoConfig from '../../../components/MontagemPaletes/EnvioAutomaticoConfig';
import '../MontagemPaletes.css';

interface ClienteOption {
  id: number;
  razao_social: string;
  cnpj: string;
}

interface UsuarioOption {
  id: number;
  nome: string;
  tipo_entidade?: 'usuario' | 'conferente'; // Para diferenciar na submissão
}

const MontagemPaleteCreate: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clientes, setClientes] = useState<ClienteOption[]>([]);
  const [filiais, setFiliais] = useState<Filial[]>([]);
  const [areas, setAreas] = useState<AreaEntrega[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioOption[]>([]);

  const [formData, setFormData] = useState({
    cliente_id: '' as string | number,
    filial_id: '' as string | number,
    local_pre_carregamento: 'cliente' as LocalPreCarregamento,
    observacoes: '',
    conferente_id: '' as string | number
  });

  // Envio automático
  const [enviosAutomaticos, setEnviosAutomaticos] = useState<EnvioConfig[]>([]);
  const [, setEnvioLoading] = useState(false);

  // Paletes a adicionar
  const [paletes, setPaletes] = useState<PaleteCreateData[]>([]);
  const [novoPalete, setNovoPalete] = useState({
    area_entrega_id: '' as string | number,
    doca: '',
    tipo_palete: 'normal' as TipoPalete,
    data_agendamento: ''
  });

  // Próximo número de palete (carregado do banco, usado apenas para exibição)
  const [proximoNumero, setProximoNumero] = useState<number | null>(null);

  // Configuração de doca
  const [exibirDoca, setExibirDoca] = useState(true);

  // Seleção múltipla de áreas
  const [areasSelecionadas, setAreasSelecionadas] = useState<number[]>([]);
  const [docaPadrao, setDocaPadrao] = useState('');
  const [tipoPaletePadrao, setTipoPaletePadrao] = useState<TipoPalete>('normal');
  const [dataAgendamentoPadrao, setDataAgendamentoPadrao] = useState('');
  const [modoSelecaoMultipla, setModoSelecaoMultipla] = useState(false);

  // Callback para quando o usuário troca o cliente manualmente
  const handleClienteChange = (novoClienteId: string) => {
    setFormData(prev => ({ ...prev, cliente_id: novoClienteId }));
    setAreasSelecionadas([]);
    setPaletes([]);
  };

  useEffect(() => {
    listarClientes({ limit: 200, ativo: 'true' }).then(res => {
      if (res.success && res.clientes) {
        setClientes(res.clientes.map((c: any) => ({ id: c.id, razao_social: c.razao_social, cnpj: c.cnpj })));
      }
    }).catch((err: any) => setError(err?.message || 'Erro ao carregar clientes'));

    listarFiliais({ ativo: 'true' }).then(res => {
      if (res.success) setFiliais(res.filiais);
    }).catch((err: any) => setError(err?.message || 'Erro ao carregar filiais'));

    listarAreas({ ativo: 'true' }).then(res => {
      if (res.success) setAreas(res.areas);
    }).catch((err: any) => setError(err?.message || 'Erro ao carregar áreas'));

    // Carregar conferentes de ambas entidades (usuarios e nova tabela conferentes)
    Promise.all([
      userService.getUsers({ limit: 200, ativo: true, tipo: 'conferente' }).catch(() => ({ usuarios: [] })),
      listarConferentes({ limit: 200, status: 'aprovado' }).catch(() => ({ success: false, conferentes: [] }))
    ]).then(([usersRes, confRes]) => {
      const fromUsuarios: UsuarioOption[] = (usersRes.usuarios || []).map((u: any) => ({
        id: u.id, nome: u.nome, tipo_entidade: 'usuario' as const
      }));
      const fromConferentes: UsuarioOption[] = ((confRes as any).conferentes || []).map((c: any) => ({
        id: c.id, nome: `${c.nome} (Conferente CPF)`, tipo_entidade: 'conferente' as const
      }));
      // Prefixar IDs para evitar colisão entre tabelas (usuario ID=4 vs conferente ID=4)
      setUsuarios([...fromUsuarios, ...fromConferentes]);
    }).catch((err: any) => setError(err?.message || 'Erro ao carregar conferentes'));

    buscarConfiguracoesConferencia().then(res => {
      if (res.success) {
        setExibirDoca(res.configuracao.exibir_doca !== false);
      }
    }).catch(() => {});
  }, []);

  // Recarregar áreas quando o cliente muda (filtrar pelas áreas atendidas)
  useEffect(() => {
    const params: { ativo: string; cliente_id?: string | number } = { ativo: 'true' };
    if (formData.cliente_id) {
      params.cliente_id = formData.cliente_id;
    }
    listarAreas(params).then(res => {
      if (res.success) {
        setAreas(res.areas);
      }
    }).catch(() => {});
  }, [formData.cliente_id]);

  // Carregar próximo número de palete (apenas para exibição, não bloqueia a operação)
  useEffect(() => {
    obterProximoNumeroPalete().then(res => {
      if (res.success && res.proximo_numero != null) {
        const num = Number(res.proximo_numero);
        if (!isNaN(num)) setProximoNumero(num);
      }
    }).catch(() => {});
  }, []);

  // Filtrar áreas pela filial selecionada
  const areasFiltradas = formData.filial_id
    ? areas.filter(a => !a.filial_id || a.filial_id === Number(formData.filial_id))
    : areas;

  const adicionarPaleteUnico = () => {
    if (!novoPalete.area_entrega_id) return;
    const areaId = Number(novoPalete.area_entrega_id);
    setPaletes(prev => [...prev, {
      area_entrega_id: areaId,
      doca: novoPalete.doca || undefined,
      tipo_palete: novoPalete.tipo_palete,
      data_agendamento: novoPalete.tipo_palete === 'agendamento' && novoPalete.data_agendamento ? novoPalete.data_agendamento : undefined
    }]);
    setNovoPalete({ area_entrega_id: '', doca: '', tipo_palete: 'normal', data_agendamento: '' });
    setError('');
  };

  const adicionarPaletesMultiplos = () => {
    if (areasSelecionadas.length === 0) return;
    const novos = areasSelecionadas.map(areaId => ({
      area_entrega_id: areaId,
      doca: docaPadrao || undefined,
      tipo_palete: tipoPaletePadrao,
      data_agendamento: tipoPaletePadrao === 'agendamento' && dataAgendamentoPadrao ? dataAgendamentoPadrao : undefined
    }));
    setPaletes(prev => [...prev, ...novos]);
    setAreasSelecionadas([]);
    setDocaPadrao('');
    setTipoPaletePadrao('normal');
    setDataAgendamentoPadrao('');
    setModoSelecaoMultipla(false);
    setError('');
  };

  const removerPalete = (index: number) => {
    setPaletes(prev => prev.filter((_, i) => i !== index));
  };

  const toggleAreaSelecionada = (areaId: number) => {
    setAreasSelecionadas(prev =>
      prev.includes(areaId) ? prev.filter(id => id !== areaId) : [...prev, areaId]
    );
  };

  const selecionarTodasAreas = () => {
    if (areasSelecionadas.length === areasFiltradas.length) {
      setAreasSelecionadas([]);
    } else {
      setAreasSelecionadas(areasFiltradas.map(a => a.id));
    }
  };

  const getAreaNome = (areaId: number) => {
    const area = areas.find(a => a.id === areaId);
    return area ? area.nome : `Área #${areaId}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Determinar se o conferente selecionado é da nova entidade ou do usuarios
      // O value do select tem formato "tipo_id" (ex: "usuario_4" ou "conferente_3")
      let conferenteTipo = '';
      let conferenteIdNum: number | null = null;
      if (formData.conferente_id && typeof formData.conferente_id === 'string' && formData.conferente_id.includes('_')) {
        const [tipo, idStr] = formData.conferente_id.split('_');
        conferenteTipo = tipo;
        conferenteIdNum = parseInt(idStr);
      }

      const payload: any = {
        cliente_id: formData.cliente_id ? Number(formData.cliente_id) : undefined,
        filial_id: formData.filial_id ? Number(formData.filial_id) : undefined,
        local_pre_carregamento: formData.local_pre_carregamento,
        observacoes: formData.observacoes || undefined,
        paletes
      };

      if (conferenteTipo === 'conferente' && conferenteIdNum) {
        payload.novo_conferente_id = conferenteIdNum;
        payload.conferente_id = null;
      } else if (conferenteTipo === 'usuario' && conferenteIdNum) {
        payload.conferente_id = conferenteIdNum;
        payload.novo_conferente_id = null;
      }
      const result = await criarOperacao(payload);
      if (result.success) {
        // Salvar configs de envio e enviar documentos automaticamente se configurado
        if (enviosAutomaticos.length > 0) {
          setEnvioLoading(true);
          try {
            // Salvar configs de envio vinculadas à operação para uso futuro
            // (quando novos paletes forem adicionados no pré-carregamento)
            await salvarEnviosOperacao(result.operacao.id, enviosAutomaticos);
            // Enviar documentos agora para os paletes atuais
            await enviarDocumentoOperacao(result.operacao.id, enviosAutomaticos);
          } catch (envioErr) {
            console.error('Erro ao enviar documentos:', envioErr);
            setError('Operação criada, mas houve erro ao enviar documentos automaticamente. Verifique os envios manualmente.');
          } finally {
            setEnvioLoading(false);
          }
        }
        navigate(`/montar-carga/${result.operacao.id}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao criar operação');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <button className="montagem-back-link" onClick={() => navigate('/montar-carga')}>
        <i className="fas fa-arrow-left"></i> Voltar
      </button>

      <div className="card">
        <div className="card-header">
          <h2><i className="fas fa-pallet" style={{ marginRight: 8 }}></i> Nova Operação de Montagem</h2>
        </div>
        <div className="card-body">
          {error && <div className="montagem-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            {/* Dados da Operação */}
            <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#2c3e50', marginBottom: 16, borderBottom: '1px solid #ecf0f1', paddingBottom: 8 }}>
              <i className="fas fa-info-circle" style={{ marginRight: 8, color: '#3498db' }}></i>
              Dados da Operação
            </h3>

            <div className="montagem-form-grid">
              <div className="form-group">
                <label>Cliente</label>
                <select
                  className="form-control"
                  value={formData.cliente_id}
                  onChange={(e) => handleClienteChange(e.target.value)}
                >
                  <option value="">Selecione um cliente</option>
                  {clientes.map(c => (
                    <option key={c.id} value={c.id}>{c.razao_social} - {c.cnpj}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Filial *</label>
                <select
                  className="form-control"
                  value={formData.filial_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, filial_id: e.target.value }))}
                  required
                >
                  <option value="">Selecione uma filial</option>
                  {filiais.map(f => (
                    <option key={f.id} value={f.id}>{f.nome_fantasia || f.razao_social} - {f.cnpj}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Local do Pré-Carregamento *</label>
                <select
                  className="form-control"
                  value={formData.local_pre_carregamento}
                  onChange={(e) => setFormData(prev => ({ ...prev, local_pre_carregamento: e.target.value as LocalPreCarregamento }))}
                  required
                >
                  <option value="cliente">No Cliente</option>
                  <option value="base_transportadora">Base da Transportadora</option>
                </select>
              </div>

              <div className="form-group">
                <label>Conferente</label>
                <select
                  className="form-control"
                  value={formData.conferente_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, conferente_id: e.target.value }))}
                >
                  <option value="">Selecione um conferente</option>
                  {usuarios.map(u => (
                    <option key={`${u.tipo_entidade}_${u.id}`} value={`${u.tipo_entidade}_${u.id}`}>{u.nome}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Observações</label>
                <textarea
                  className="form-control"
                  value={formData.observacoes}
                  onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                  rows={2}
                  placeholder="Observações gerais da operação..."
                />
              </div>
            </div>

            {/* Paletes */}
            <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#2c3e50', marginTop: 32, marginBottom: 16, borderBottom: '1px solid #ecf0f1', paddingBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                <i className="fas fa-boxes-stacked" style={{ marginRight: 8, color: '#8e44ad' }}></i>
                Paletes ({paletes.length})
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={() => setModoSelecaoMultipla(!modoSelecaoMultipla)}
                style={{ fontSize: '0.8rem' }}
              >
                <i className={`fas ${modoSelecaoMultipla ? 'fa-minus' : 'fa-th-list'}`}></i>
                {modoSelecaoMultipla ? 'Modo Individual' : 'Selecionar Múltiplas Áreas'}
              </button>
            </h3>

            {/* Modo Seleção Múltipla */}
            {modoSelecaoMultipla ? (
              <div className="palete-form-inline" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    Selecione as áreas de entrega ({areasSelecionadas.length} selecionada{areasSelecionadas.length !== 1 ? 's' : ''})
                  </label>
                  <button type="button" className="btn btn-sm btn-outline" onClick={selecionarTodasAreas} style={{ fontSize: '0.75rem' }}>
                    {areasSelecionadas.length === areasFiltradas.length ? 'Desmarcar Todas' : 'Selecionar Todas'}
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 200, overflowY: 'auto', padding: 4 }}>
                  {areasFiltradas.map(area => (
                    <label
                      key={area.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                        borderRadius: 8, border: `2px solid ${areasSelecionadas.includes(area.id) ? '#3498db' : '#dce1e6'}`,
                        background: areasSelecionadas.includes(area.id) ? '#ebf5fb' : 'white',
                        cursor: 'pointer', fontSize: '0.85rem', transition: 'all 0.2s'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={areasSelecionadas.includes(area.id)}
                        onChange={() => toggleAreaSelecionada(area.id)}
                      />
                      {area.nome} {area.regiao ? `(${area.regiao})` : ''}
                    </label>
                  ))}
                </div>
                <div className="form-row">
                  {exibirDoca && (
                    <div className="form-group">
                      <label>Doca (aplicar a todos)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={docaPadrao}
                        onChange={(e) => setDocaPadrao(e.target.value)}
                        placeholder="Ex: DOCA 01"
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Tipo (aplicar a todos)</label>
                    <select
                      className="form-control"
                      value={tipoPaletePadrao}
                      onChange={(e) => setTipoPaletePadrao(e.target.value as TipoPalete)}
                    >
                      <option value="normal">Normal</option>
                      <option value="agendamento">Agendamento</option>
                    </select>
                  </div>
                  {tipoPaletePadrao === 'agendamento' && (
                    <div className="form-group">
                      <label>Data/Hora Agendamento</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={dataAgendamentoPadrao}
                        onChange={(e) => setDataAgendamentoPadrao(e.target.value)}
                      />
                    </div>
                  )}
                </div>
                <div className="form-actions">
                  <button type="button" className="btn btn-primary" onClick={adicionarPaletesMultiplos} disabled={areasSelecionadas.length === 0}>
                    <i className="fas fa-plus"></i> Adicionar {areasSelecionadas.length} Palete{areasSelecionadas.length !== 1 ? 's' : ''}
                  </button>
                </div>
              </div>
            ) : (
              /* Modo Individual */
              <div className="palete-form-inline" style={{ marginBottom: 16 }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Área de Entrega</label>
                    <select
                      className="form-control"
                      value={novoPalete.area_entrega_id}
                      onChange={(e) => setNovoPalete(prev => ({ ...prev, area_entrega_id: e.target.value }))}
                    >
                      <option value="">Selecione uma área</option>
                      {areasFiltradas.map(area => (
                        <option key={area.id} value={area.id}>
                          {area.nome} {area.regiao ? `(${area.regiao})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  {exibirDoca && (
                    <div className="form-group">
                      <label>Doca</label>
                      <input
                        type="text"
                        className="form-control"
                        value={novoPalete.doca}
                        onChange={(e) => setNovoPalete(prev => ({ ...prev, doca: e.target.value }))}
                        placeholder="Ex: DOCA 01"
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Tipo</label>
                    <select
                      className="form-control"
                      value={novoPalete.tipo_palete}
                      onChange={(e) => setNovoPalete(prev => ({ ...prev, tipo_palete: e.target.value as TipoPalete }))}
                    >
                      <option value="normal">Normal</option>
                      <option value="agendamento">Agendamento</option>
                    </select>
                  </div>
                  {novoPalete.tipo_palete === 'agendamento' && (
                    <div className="form-group">
                      <label>Data/Hora Agendamento</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={novoPalete.data_agendamento}
                        onChange={(e) => setNovoPalete(prev => ({ ...prev, data_agendamento: e.target.value }))}
                      />
                    </div>
                  )}
                </div>
                <div className="form-actions">
                  <button type="button" className="btn btn-primary" onClick={adicionarPaleteUnico} disabled={!novoPalete.area_entrega_id}>
                    <i className="fas fa-plus"></i> Adicionar Palete
                  </button>
                </div>
              </div>
            )}

            {/* Lista de paletes adicionados */}
            {paletes.length > 0 && (
              <div className="paletes-grid">
                {paletes.map((p, index) => (
                  <div key={index} className="palete-card">
                    <button type="button" className="palete-remove" onClick={() => removerPalete(index)}>
                      <i className="fas fa-times"></i>
                    </button>
                    <div className="palete-header">
                      <span className="palete-number">
                        <i className="fas fa-pallet"></i>
                        Palete #{proximoNumero != null ? proximoNumero + index : '--'}
                      </span>
                      {p.tipo_palete === 'agendamento' && (
                        <span style={{
                          padding: '2px 8px', background: '#fde8d0', color: '#a04000',
                          borderRadius: 4, fontSize: '0.7rem', fontWeight: 600
                        }}>
                          AGENDAMENTO
                        </span>
                      )}
                    </div>
                    <div className="palete-info">
                      <div className="palete-info-row">
                        <i className="fas fa-map-marker-alt"></i>
                        <span>Área: <strong>{getAreaNome(p.area_entrega_id)}</strong></span>
                      </div>
                      {p.doca && (
                        <div className="palete-info-row">
                          <i className="fas fa-warehouse"></i>
                          <span>Doca: <strong>{p.doca}</strong></span>
                        </div>
                      )}
                      {p.tipo_palete === 'agendamento' && p.data_agendamento && (
                        <div className="palete-info-row" style={{ color: '#8e44ad' }}>
                          <i className="fas fa-clock"></i>
                          <span>Agendamento: <strong>
                            {new Date(p.data_agendamento).toLocaleDateString('pt-BR')} às {new Date(p.data_agendamento).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </strong></span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Envio Automático de Documentos */}
            <EnvioAutomaticoConfig
              envios={enviosAutomaticos}
              onChange={setEnviosAutomaticos}
            />

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 32 }}>
              <button type="button" className="btn btn-outline" onClick={() => navigate('/montar-carga')}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <div className="loading-spinner" style={{ width: 16, height: 16 }}></div>
                    Criando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    Criar Operação
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MontagemPaleteCreate;
